/**
 * M3-4 — Admin scoped capability deploy (single / multi / Group).
 * No invent Cap campaign write — CTAs to OTA Cap / Groups only.
 * Mount: /machine/capabilities
 * Fleet-wide Cap stays on SA OTA Rail A — not here.
 */
import { useMemo, useState } from "react";
import { html, Alert, go, Ico } from "./ui.js";
import { path, useApi } from "./api.js";
import { edgeDevicesOf } from "./edgeos-live.js";
import { useSession } from "./session.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { CAP_HOME } from "./cmc-entry-registry.js";
import { hasTenantAdminSeat } from "./cap-complete.js";

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__devices") {
      const lim = (opts && opts.query && opts.query.limit) || 80;
      return "/devices?limit=" + encodeURIComponent(lim);
    }
    return "";
  }
}

export default function MachineCapDeploy({ toast }) {
  const { me } = useSession();
  /* CAP-COMPLETE: tenant Admin / root only — never sa_staff via caps.length. */
  const isAdmin = hasTenantAdminSeat(me);
  const reg = useApi(isAdmin ? safePath("get__devices", { query: { limit: 80 } }) : null);
  const devices = useMemo(() => edgeDevicesOf(reg.data).slice(0, 48), [reg.data]);
  const [picked, setPicked] = useState(() => new Set());
  const [groupName, setGroupName] = useState("");

  const toggle = (id) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const pickN = picked.size;
  const scopeLine = pickN
    ? `${pickN} machine(s) selected`
    : (groupName.trim() ? `Group target · ${groupName.trim()}` : "No scope yet");

  const openRollout = () => {
    toast && toast("Opening capability rollout with this scope", "go");
    confirmLeaveSeat({
      fromRoute: "/machine/capabilities",
      targetPath: CAP_HOME.ota_capability_rollout.path,
    });
  };

  if (!isAdmin) {
    return html`<div className="sheet machine-cap desk-craft" data-cmc-m3="m3-4" data-cmc-shell="machine-ux">
      <${Alert} tone="bad" title="Scoped Cap sealed"
        say="Only Admin or Platform seats can stage capability deploys. Capability packs are not invented here."/>
      <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/machine")}>
        Machine list</button>
    </div>`;
  }

  return html`<div className="sheet machine-cap machine-deck machine-hub desk-craft" data-cmc-m3="m3-4"
      data-cmc-shell="machine-ux" data-cmc-machine-flat="1">
    <div className="wrap machine-hub__in">
      <header className="machine-hub__chrome" aria-label="Scoped Cap">
        <div className="machine-hub__mast">
          <div className="machine-hub__id">
            <p className="eyebrow">Hardware</p>
            <h1 className="machine-hub__title">Scoped Cap</h1>
            <small className="machine-v2__lede">Stage packs for one machine, multi-select, or a Group</small>
          </div>
          <nav className="machine-hub__seats" aria-label="Machine seats">
            <button type="button" className="machine-hub__seat" onClick=${() => go("/machine")}>List</button>
            <button type="button" className="machine-hub__seat" onClick=${() => go("/machine/groups")}>Groups</button>
            <button type="button" className="machine-hub__seat" data-on="1">Scoped Cap</button>
          </nav>
        </div>
        <div className="machine-hub__tools">
          <button type="button" className="btn btn--go btn--sm" onClick=${openRollout}>Open Cap rollout</button>
        </div>
      </header>
      <p className="note machine-hub__note">
        Publish happens on Cap rollout after a real scope. Fleet-wide Cap stays on OTA.
      </p>
    <div className="ops-onto__kpis" style=${{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}>
      <div className="ops-onto__kpi"><em>Scope</em><strong>${scopeLine}</strong></div>
      <div className="ops-onto__kpi"><em>Nodes</em><strong>${devices.length}</strong></div>
    </div>
    <label className="ops-onto__filter" style=${{ display: "block", marginBottom: 12 }}>
      Group name (optional · membership from Machine Groups)
      <input className="inp" value=${groupName} placeholder="e.g. gate-north"
        onInput=${(e) => setGroupName(e.target.value)} style=${{ marginTop: 6, width: "100%", maxWidth: 360 }}/>
    </label>
    <div className="machine-deck__pick" role="listbox" aria-label="Pick machines for Cap" style=${{ marginBottom: 14 }}>
      ${!devices.length
        ? html`<div className="machine-deck__empty">${reg.loading ? "Reading nodes…" : "No nodes on this account — empty stays empty."}</div>`
        : devices.map((d) => html`
            <button key=${d.id} type="button" role="option" className="machine-deck__node"
              aria-selected=${picked.has(d.id) ? "true" : "false"}
              data-on=${picked.has(d.id) ? "1" : "0"}
              data-live=${d.online ? "1" : "0"}
              onClick=${() => toggle(d.id)}>
              <i className="machine-deck__node-dot"/>
              <b>${d.label}</b>
              <span>${d.id}</span>
              <em>${picked.has(d.id) ? "picked" : (d.online ? "online" : "quiet")}</em>
            </button>`)}
    </div>
    <${Alert} tone="warn" title="Publish stays on Cap rollout"
      say=${`Scope: ${scopeLine}. Next step opens Cap rollout — never claim deployed without a real campaign row.`}/>
    <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginTop: 10 }}>
      <button type="button" className="btn btn--go btn--sm"
        disabled=${!pickN && !groupName.trim()}
        onClick=${openRollout}>
        <${Ico} d="chip" w=${13}/> Open Cap rollout · scoped</button>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!groupName.trim()}
        onClick=${() => go("/machine/groups/" + encodeURIComponent(groupName.trim()))}>
        Open Group</button>
      <button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => confirmLeaveSeat({
          fromRoute: "/machine/capabilities",
          targetPath: CAP_HOME.ota_capability_rollout.path,
          objectLabel: "Fleet-wide Cap",
        })}>
        Fleet-wide Cap</button>
      ${pickN === 1 ? html`<button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => go("/machine/" + encodeURIComponent([...picked][0]))}>
        Open Machine detail</button>` : null}
    </div>
    </div>
  </div>`;
}
